import { FC, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";

import { MIN_GROUP_COUNT } from "../../../utils/breakouts";

import { BreakoutGroupKind } from "components/server-types";
import { t } from "i18n/i18n";
import { Listbox } from "mds/components/Listbox";
import { Tooltip } from "mds/components/Tooltip";
import { PageIcon, PageListIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import {
  selectCurrentSectionId,
  selectGroupCountsByPageId,
  selectGroupPagesForCurrentSection,
  topicSelectors,
} from "store/selectors";
import { useFullObjectSelector, useListSelector } from "store/store-hooks";
import { filterMap } from "utils/collections";

type FormValues = {
  breakout_group_type: BreakoutGroupKind;
  group_page_id: string | null;
  should_sync_breakouts_with_page_groups: boolean;
};

export const GroupPageDropdown: FC = () => {
  // string    → a topicId has been selected
  // null      → no topicId selected yet (initial state)
  // undefined → user is viewing group pages that are not topic-based (e.g., assignments)
  const [topicId, setTopicId] = useState<string | null | undefined>(null);

  const form = useFormContext<FormValues>();
  const breakoutGroupType = form.watch("breakout_group_type");
  const groupPageId = form.watch("group_page_id");
  const shouldSyncBreakoutsWithPageGroups = form.watch("should_sync_breakouts_with_page_groups");

  const sectionId = useAppSelector(selectCurrentSectionId);
  const pages = useListSelector(selectGroupPagesForCurrentSection, ["id", "topic_id", "title"]);
  const topics = useListSelector(topicSelectors.filterForCurrentSection, ["id", "title"]);
  const pageGroupCountByPageId = useFullObjectSelector(selectGroupCountsByPageId);

  const isGroupPageBreakoutGroupType = breakoutGroupType === "page";

  useEffect(() => {
    if (!sectionId) return;
    storeApi.page_groups.list({ section_id: sectionId });
  }, [sectionId]);

  const topicOptions = useMemo(
    () =>
      topics.map((topic) => ({
        title: topic.title,
        id: topic.id,
        iconStart: <PageListIcon />,
      })),
    [topics],
  );

  const topicOptionsWithNoTopicOption = [
    ...topicOptions,
    {
      title: t("breakout_group_modal.no_topic"),
      id: "none",
    },
  ];

  const pageOptions = useMemo(
    () =>
      filterMap(
        pages,
        (page) =>
          page.topic_id === topicId &&
          (shouldSyncBreakoutsWithPageGroups ||
            (pageGroupCountByPageId[page.id] ?? 0) >= MIN_GROUP_COUNT),
        (page) => ({
          title: page.title,
          id: page.id,
          iconStart: <PageIcon />,
        }),
      ),
    [pages, topicId, pageGroupCountByPageId, shouldSyncBreakoutsWithPageGroups],
  );

  const isGroupPageDropdownDisabled =
    !isGroupPageBreakoutGroupType || topicId === null || pageOptions.length === 0;

  return (
    <div className="flex gap-2">
      <Listbox
        buttonText={topicId === null ? t("breakout_group_modal.select_a_topic") : undefined}
        className="ml-6 !h-full !w-full !max-w-[43%] !justify-between !p-1.5"
        disabled={!isGroupPageBreakoutGroupType}
        eventName="Breakouts Setup - Select a topic"
        items={topicOptionsWithNoTopicOption}
        selectedValue={topicId || undefined}
        onSelectionChange={(selectedTopic) => {
          if (selectedTopic.id !== topicId) {
            form.setValue("group_page_id", null);
          }
          setTopicId(selectedTopic.id);
        }}
      />

      <div className="mr-6 !w-full !max-w-[43%]">
        <Tooltip
          content={isGroupPageDropdownDisabled ? t("tooltip.no_group_page_available") : undefined}
          wrapperClassName="flex w-full [&>.mds-tooltip-source]:w-full"
        >
          <Listbox
            buttonText={!groupPageId ? t("breakout_group_modal.select_a_group_page") : undefined}
            className="!h-full !w-full !justify-between !p-1.5"
            disabled={isGroupPageDropdownDisabled}
            eventName="Breakouts Setup - Select a group page"
            eventPropertyKey="pageId"
            items={pageOptions}
            selectedValue={groupPageId || undefined}
            onSelectionChange={(selectedPage) => {
              if (selectedPage.id) {
                form.clearErrors("group_page_id");
              }
              form.setValue("group_page_id", selectedPage.id);
            }}
          />
        </Tooltip>
      </div>
    </div>
  );
};
